﻿import { ApiCaller } from 'apis/ApiCaller';

export type OrderDetails = {
    AssignmentName: number;
    EnrollmentType: number;
    Id: number;
    UsesRedeemed: number;
};

export type SupplierOption = {
    Id: number;
    Name: string;
    Type: number;
};

export type License = {
    AllowOffline: boolean;
    DateCreate: Date;
    DaysExpire: Date;
    DeliverableTypeString: string;
    Detailkey: number;
    Expanded: boolean;
    ID: number;
    Id: number;
    LicenseType: string;
    LicenseTypeString: string;
    MaxUses: string;
    Name: string;
    Prefix: string;
    RowInCellEditMode: boolean;
    SuppliedBy: number;
    SuppliedByName: string;
    SupplierOptions: SupplierOption[];
    UsageDays: number;
};

/** Flat result returned by the Search endpoint (matches WPL3_SearchLicenses output). */
export type LicenseSearchResult = {
    DateCreate: string;
    DeliverableTypeString: string;
    ID: number;
    Id: number;
    LicenseDuration: number;
    LicenseType: string;
    Name: string;
};

/** One deliverable entry inside a LicenseDetail (Create/Edit form). */
export type LicenseDeliverable = {

    // CSV of content IDs, e.g. "1,2,3,"
    ContentIds: string;

    // 1=PracticeTest, 2=EBook, 3=Course
    DeliverableType: number;
    MaxSeatsPerUser: number;
    MaxUses: number;
    Seats: number;
    UsesPerCode: number;
};

/** Full license detail returned by GetById for pre-populating the edit form. */
export type LicenseDetail = {
    AllowAssociate: number;
    AllowOffline: boolean;
    Deliverables: LicenseDeliverable[];
    Id: number;
    LicenseDuration: number;
    LicenseType: number;
    Name: string;
    Prefix: string;
    UsageDays: number;
};

/** Per-deliverable slice of the create/update form payload. */
export type DeliverablePayload = {
    contentIds: number[];
    maxSeatsPerUser: number;
    maxUses: number;
    seats: number;
    usesPerCode: number;
};

/** Full payload sent to Create / Update endpoints. */
export type LicenseFormPayload = {

    /** Only required for Update. */
    id?: number;
    name: string;
    allowAssociate: number;
    allowOffline: boolean;
    books: DeliverablePayload;
    courses: DeliverablePayload;
    licenseDuration: number;

    // 1=Site, 2=Individual, 3=Seat
    licenseType: number;
    practiceTests: DeliverablePayload;
    prefix: string;
    usageDays: number;
};

/**
 * Per-deliverable slice of the Update payload. Instead of the full content list it
 * carries only the delta — the IDs added and removed since the form was loaded — so the
 * request stays small enough to clear the MVC model binder's 2 MB JSON cap. The server
 * reads the current lists from the DB and applies this delta to rebuild the full list.
 */
export type DeliverableDiffPayload = {
    addedIds: number[];
    maxSeatsPerUser: number;
    maxUses: number;
    removedIds: number[];
    seats: number;
    usesPerCode: number;
};

/** Diff-based payload sent to the Update endpoint. */
export type LicenseUpdatePayload = {
    id: number;
    name: string;
    allowAssociate: number;
    allowOffline: boolean;
    books: DeliverableDiffPayload;
    courses: DeliverableDiffPayload;
    licenseDuration: number;

    // 1=Site, 2=Individual, 3=Seat
    licenseType: number;
    practiceTests: DeliverableDiffPayload;
    prefix: string;
    usageDays: number;
};

export type Product = {
    Category: { Id: number; Name: string; Version: string };
    Color: string;
    IconURL: string;
    Id: number;
    Name: string;
};

export type Course = {
    Category: { Id: number; Name: string; Version: string };
    Id: number;
    Name: string;
};

export type Book = {
    Active: number;
    Category: { Id: number; Name: string; Version: string };
    Id: number;
    IdProduct: number;
    Name: string;
};

/**
 * Represents an api handler for the LicenseController.
 */
export class LicenseApi {

    private static apiCaller = new ApiCaller('License');

    public static GetAll(cultureId: number, childPartnerId = 0, parentPartnerId = 0): Promise<License[]> {

        return this.apiCaller.GET<License[]>('GetAll', { childPartnerId, cultureId, parentPartnerId });

    }

    public static Get(licenseName: string) {

        return this.apiCaller.GET<License[]>('Get', { licenseName });

    }

    /** Full-param search used by the React list view. */
    public static Search(name: string, expire: number, type: number): Promise<LicenseSearchResult[]> {

        return this.apiCaller.GET<LicenseSearchResult[]>('Search', { name, expire, type });

    }

    /** Load a single license with full deliverable detail for the edit form. */
    public static GetById(id: number): Promise<LicenseDetail> {

        return this.apiCaller.GET<LicenseDetail>('GetById', { id });

    }

    /** Create a new license. */
    public static Create(payload: LicenseFormPayload): Promise<{ Done: boolean; Message: string }> {

        return this.apiCaller.POST('Create', {
            AllowAssociate: payload.allowAssociate,
            AllowOffline: payload.allowOffline,
            Books: { ContentIds: payload.books.contentIds, MaxSeatsPerUser: payload.books.maxSeatsPerUser, MaxUses: payload.books.maxUses, Seats: payload.books.seats, UsesPerCode: payload.books.usesPerCode },
            Courses: { ContentIds: payload.courses.contentIds, MaxSeatsPerUser: payload.courses.maxSeatsPerUser, MaxUses: payload.courses.maxUses, Seats: payload.courses.seats, UsesPerCode: payload.courses.usesPerCode },
            LicenseDuration: payload.licenseDuration,
            LicenseType: payload.licenseType,
            Name: payload.name,
            PracticeTests: { ContentIds: payload.practiceTests.contentIds, MaxSeatsPerUser: payload.practiceTests.maxSeatsPerUser, MaxUses: payload.practiceTests.maxUses, Seats: payload.practiceTests.seats, UsesPerCode: payload.practiceTests.usesPerCode },
            Prefix: payload.prefix ?? '',
            UsageDays: payload.usageDays
        });

    }

    /** Update an existing license. Sends only the content-ID delta per deliverable. */
    public static Update(payload: LicenseUpdatePayload): Promise<{ Done: boolean; Message: string }> {

        return this.apiCaller.POST('Update', {
            AllowAssociate: payload.allowAssociate,
            AllowOffline: payload.allowOffline,
            Books: { AddedIds: payload.books.addedIds, MaxSeatsPerUser: payload.books.maxSeatsPerUser, MaxUses: payload.books.maxUses, RemovedIds: payload.books.removedIds, Seats: payload.books.seats, UsesPerCode: payload.books.usesPerCode },
            Courses: { AddedIds: payload.courses.addedIds, MaxSeatsPerUser: payload.courses.maxSeatsPerUser, MaxUses: payload.courses.maxUses, RemovedIds: payload.courses.removedIds, Seats: payload.courses.seats, UsesPerCode: payload.courses.usesPerCode },
            Id: payload.id,
            LicenseDuration: payload.licenseDuration,
            LicenseType: payload.licenseType,
            Name: payload.name,
            PracticeTests: { AddedIds: payload.practiceTests.addedIds, MaxSeatsPerUser: payload.practiceTests.maxSeatsPerUser, MaxUses: payload.practiceTests.maxUses, RemovedIds: payload.practiceTests.removedIds, Seats: payload.practiceTests.seats, UsesPerCode: payload.practiceTests.usesPerCode },
            Prefix: payload.prefix,
            UsageDays: payload.usageDays
        });

    }

    public static GetProduct() {

        return this.apiCaller.GET<Product[]>('GetProducts');

    }

    public static GetCourse() {

        return this.apiCaller.GET<Course[]>('GetCourses');

    }

    public static GetBook() {

        return this.apiCaller.GET<Book[]>('GetBooks');

    }

    public static GetLicensesByIncludeExclude(
        contentType: string,
        includeIds: number[],
        excludeIds: number[]
    ): Promise<License[]> {

        switch (contentType) {

            case 'Product':
                return this.apiCaller.GET<License[]>('GetLicensesByTestProductsIncludeExclude', {
                    excludeIds,
                    includeIds
                });
            case 'Course':
                return this.apiCaller.GET<License[]>('GetLicensesByCoursesIncludeExclude', { excludeIds, includeIds });
            case 'Book':
                return this.apiCaller.GET<License[]>('GetLicensesByBooksIncludeExclude', { excludeIds, includeIds });
            default:
                return this.apiCaller.GET<License[]>('GetLicensesByTestProductsIncludeExclude', {
                    excludeIds,
                    includeIds
                });

        }

    }

    public static AddContentToLicenses(contentType: string, productIds: number[], licenseIds: number[]) {

        switch (contentType) {

            case 'Product':
                return this.apiCaller.GET<License[]>('AddProductToLicenses', { licenseIds, productIds });
            case 'Course':
                return this.apiCaller.GET<License[]>('AddCourseToLicenses', { licenseIds, productIds });
            case 'Book':
                return this.apiCaller.GET<License[]>('AddbookToLicenses', { licenseIds, productIds });
            default:
                return this.apiCaller.GET<License[]>('AddProductToLicenses', { licenseIds, productIds });

        }

    }

    public static GetOrderUsageDetails(orderId: number, cultureId: number) {

        return this.apiCaller.GET<OrderDetails[]>('GetOrderUsageDetails', { cultureId, orderId });

    }

}
